import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { notifySuccess } from '../../services/notify';
import type { Tag } from '../useProjectTags';

export interface TagFormElement {
  id?: string | null;
  sortOrder?: number;
  __className?: string;
  type: 'radio' | 'checkbox' | 'text';
  tagId?: string;
  tag?: Tag;
  inverseTag?: Tag | null;
  children?: Tag[] | null;
  inverseChildren?: Tag[] | null;
  exclusions?: Tag[] | null;
  name?: string; // text elements only
}

export interface TagForm {
  id?: string;
  projectId: string;
  roleId: string;
  elements: TagFormElement[];
  saving?: boolean;
}

const TAG_ELEMENT_CLASS = 'io.indexed.domainmodel.tagging.TagFormElementTag';
const TEXT_ELEMENT_CLASS = 'io.indexed.domainmodel.tagging.TagFormElementText';

export function makeTagElement(fields: Pick<TagFormElement, 'type'> & Omit<Partial<TagFormElement>, 'type'>): TagFormElement {
  return {
    id: null,
    sortOrder: 0,
    __className: TAG_ELEMENT_CLASS,
    exclusions: [],
    children: null,
    inverseTag: null,
    inverseChildren: null,
    ...fields,
  };
}

export function makeTextElement(): TagFormElement {
  return { id: null, sortOrder: 0, __className: TEXT_ELEMENT_CLASS, type: 'text', name: '' };
}

export interface TagFormAvailableTag {
  type: 'radio' | 'checkbox';
  tag: Tag;
  inverseTag: Tag | null;
  tagId: string;
}

export interface TagFormAvailableTagsPage {
  elements: TagFormAvailableTag[];
  totalTags: number;
  totalPages: number;
}

export function useTagForm(projectId: string, roleId: string) {
  return useQuery<TagForm>({
    queryKey: ['tagForm', projectId, roleId],
    queryFn: async () => {
      try {
        const form = await api.get<TagForm>(`/project/${projectId}/tagform/role/${roleId}`);
        if (form?.id) return form;
      } catch (e: any) {
        if (!/→ 404/.test(e?.message)) throw e;
      }
      // No form exists yet for this role — return an empty one (mirrors Angular's createTagForm)
      return { projectId, roleId, elements: [] };
    },
    enabled: !!projectId && !!roleId,
  });
}

function normalizeForm(tagForm: TagForm): TagForm {
  return {
    ...tagForm,
    elements: tagForm.elements.map(el => ({
      ...el,
      __className: el.__className ?? (el.type === 'text' ? TEXT_ELEMENT_CLASS : TAG_ELEMENT_CLASS),
    })),
  };
}

export function useSaveTagForm(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagForm: TagForm) => {
      const normalized = normalizeForm(tagForm);
      return normalized.id
        ? api.put<TagForm>(`/project/${projectId}/tagform/${normalized.id}`, normalized)
        : api.post<TagForm>(`/project/${projectId}/tagform`, normalized);
    },
    onSuccess: (saved, variables) => {
      queryClient.setQueryData(
        ['tagForm', projectId, variables.roleId],
        saved ? { ...variables, ...saved } : variables,
      );
      notifySuccess({ title: 'Tag Form Saved', timeout: 2500 });
    },
  });
}

export function useTagSuggestions(projectId: string, tagName: string, hasInverse: boolean) {
  return useQuery<TagFormElement['tag'][]>({
    queryKey: ['tagSuggestions', projectId, tagName, hasInverse],
    queryFn: () =>
      api.get<TagFormElement['tag'][]>(
        `/project/${projectId}/tag/suggest?tagName=${encodeURIComponent(tagName)}&categoryBitmask=4&limit=15&hasInverse=${hasInverse}&checkInverse=true&isChild=false&includeSubTags=true`
      ),
    enabled: !!projectId && tagName.length > 0,
    placeholderData: [],
  });
}

export function useTagFormAvailableTags(
  projectId: string,
  excludedIds: string[],
  page: number
) {
  return useQuery<TagFormAvailableTagsPage>({
    queryKey: ['tagFormAvailableTags', projectId, excludedIds, page],
    queryFn: () =>
      api.get<TagFormAvailableTagsPage>(
        `/project/${projectId}/category/0/tagform?page=${page - 1}&limit=20&existingIds=${excludedIds.join(',')}`
      ),
    enabled: !!projectId,
  });
}
